import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { TimeHlp } from 'src/lib/fliko/helpers/time.helper';
import { UserHlp } from 'src/lib/fliko/helpers/user.helper';
import { AgreementTenant } from 'src/lib/fliko/models/agreements/personAgr.data';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { TenancyAgreementData, TenancyDetailsBase } from 'src/lib/fliko/models/agreements/tenancyAgr.data';
import { ResidentType, ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { RentalUnitType, RentalUnit } from 'src/lib/fliko/models/units/unit.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { GrammarHelper } from 'src/app/shared/helpers/grammar.helper';
import { UserHelper } from 'src/app/shared/helpers/user-helper';
import { idMap } from 'src/app/shared/operators/idMap.operator';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { UserPRSList, UserPRSTabData } from './models/users-table.data';
import { RentalUnitVM } from './models/users-view-models';
import { AgreementService } from './agreement.service';
import { ProtocolService } from './protocol.service';
import { ResidentService } from './resident.service';
import { UnitService } from './unit.service';

@Injectable({
  providedIn: 'root'
})
export class UsersPRSDataService implements OnDestroy {
  private agreementTabData$ = new BehaviorSubject<UserPRSList.AgreementElement[] | null>(null);
  private rentalUnitsTabData$ = new BehaviorSubject<UserPRSList.RentalUnitElement[] | null>(null);
  private landlordTenantTabData$ = new BehaviorSubject<UserPRSList.ResidentElement[] | null>(null);

  private subscription$ = new Subscription();

  constructor(
    private unitService: UnitService,
    private agreementService: AgreementService,
    private protocolService: ProtocolService,
    private residentService: ResidentService,
    private errorHandlingService: ErrorHandlingService,
    private ls: LocalizationService,
    @Optional() @SkipSelf() thisService?: UsersPRSDataService
  ) {
    if (thisService) throw new Error('Multiple instances of UsersPRSDataService');
    this.subscription$.add(this.getTabData$().subscribe((data) => this.processData(data)));
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public getAgreementsTabData$(): Observable<UserPRSList.AgreementElement[] | null> {
    return this.agreementTabData$.asObservable();
  }

  public getUnitsTabData$(): Observable<UserPRSList.RentalUnitElement[] | null> {
    return this.rentalUnitsTabData$.asObservable();
  }

  public getResidentsTabData$(): Observable<UserPRSList.ResidentElement[] | null> {
    return this.landlordTenantTabData$.asObservable();
  }

  public getAgreementTabData(id: string): UserPRSList.AgreementElement | undefined {
    return this.agreementTabData$.value?.find((x) => x.id == id);
  }

  public getUnitTabData(id: string): UserPRSList.RentalUnitElement | undefined {
    return this.rentalUnitsTabData$.value?.find((x) => x.id == id);
  }

  public getResidentTabData(id: string, type: ResidentType): UserPRSList.ResidentElement | undefined {
    return this.landlordTenantTabData$.value?.find((x) => x.id == id && x.type == type);
  }

  // #region Initializers

  private getTabData$(): Observable<UserPRSTabData> {
    // HACK combine latest looses types if more than 6 args
    return combineLatest([
      this.unitService.getApartments$(),
      this.unitService.getParkings$(),
      this.unitService.getStorages$(),
      combineLatest([
        this.residentService.getResidents$().pipe(idMap()),
        this.agreementService.getPrsAgreements$(),
        this.protocolService.getAllProtocols$()
      ])
    ]).pipe(
      map(([apt, park, sto, [residents, agreements, protocols]]) => {
        const apartments = MapHlp.idMap(apt);
        const parkingSpots = MapHlp.idMap(park);
        const storages = MapHlp.idMap(sto);
        const allUnits = [...apt, ...park, ...sto];
        return { agreements, protocols, apartments, residents, parkingSpots, storages, allUnits };
      })
    );
  }

  private processData(data: UserPRSTabData): void {
    this.agreementTabData$.next(this.prepareAgreementData(data));
    this.rentalUnitsTabData$.next(this.prepareRentalUnit(data));
    this.landlordTenantTabData$.next(this.prepareLandlordTenantData(data));
  }

  private prepareAgreementData(usersData: UserPRSTabData): UserPRSList.AgreementElement[] {
    const usersAgreement: UserPRSList.AgreementElement[] = [];
    usersData.agreements.forEach((agr) => {
      const apartments: RentalUnitVM[] = [];
      const parkingSpots: RentalUnitVM[] = [];
      const storages: RentalUnitVM[] = [];
      agr.tenancyApartmentDetails?.forEach((e) => {
        const vm = this.unitsGet(usersData.apartments, RentalUnitType.APARTMENT, e.apartmentId, e);
        if (vm) apartments.push(vm);
      });
      agr.tenancyParkingSpotDetails?.forEach((e) => {
        const vm = this.unitsGet(usersData.parkingSpots, RentalUnitType.PARKING, e.parkingSpotId, e);
        if (vm) parkingSpots.push(vm);
      });
      agr.tenancyStorageDetails?.map((e) => {
        const vm = this.unitsGet(usersData.storages, RentalUnitType.STORAGE, e.storageId, e);
        if (vm) storages.push(vm);
      });
      usersAgreement.push({
        id: agr.id,
        agreement: agr,
        status: UserHlp.agreementStatus(agr),
        apartments,
        landlords: agr.landlords.map((e) => this.mapGet(usersData.residents, 'landlords', e.id)),
        tenants: agr.tenants?.map((e) => this.mapGet(usersData.residents, 'tenants', e.id)) || [],
        parkingSpots,
        storages,
        protocols: usersData.protocols.filter((e) => e.agreementId == agr.id),
        date: { from: agr.signDate || agr.agreementStartDate, to: agr.expirationDate || agr.agreementEndDate }
      });
    });
    return usersAgreement;
  }

  private prepareRentalUnit(usersData: UserPRSTabData): UserPRSList.RentalUnitElement[] {
    const usersRentalUnits: UserPRSList.RentalUnitElement[] = [];
    const today = TimeHlp.todayAt(0);
    usersData.apartments.forEach((apartment) => {
      const currentAgreements: TenancyAgreementData[] = [];
      const archivedAgreements: TenancyAgreementData[] = [];
      const futureAgreements: TenancyAgreementData[] = [];
      // TODO: function
      usersData.agreements.forEach((agreement) => {
        const details = agreement.tenancyApartmentDetails?.find((details) => details.apartmentId === apartment.id);
        if (details) {
          if (UserHlp.isCurrentAgreement(agreement) && details.rentStartDate <= today && details.rentEndDate >= today) {
            currentAgreements.push(agreement);
          } else if (details.rentStartDate > today) futureAgreements.push(agreement);
          else archivedAgreements.push(agreement);
        }
      });

      if (currentAgreements.length > 1) {
        this.errorHandlingService.showGlobalError(
          this.ls.trReplace('error.unitAgr', {
            $type: GrammarHelper.unitTypeHR(RentalUnitType.APARTMENT, EstateType.PRS, this.ls),
            $number: apartment.id
          })
        );
      }

      if (currentAgreements.length) {
        const agr = currentAgreements[0];
        const tenancyDetails = agr.tenancyApartmentDetails?.find((x) => x.apartmentId == apartment.id);
        usersRentalUnits.push({
          id: apartment.id,
          type: RentalUnitType.APARTMENT,
          agreement: agr,
          tenancyDetails: { ...tenancyDetails, unitId: apartment.id, type: RentalUnitType.APARTMENT },
          unit: apartment,
          landlords: agr.landlords.map((e) => this.mapGet(usersData.residents, 'landlords', e.id)),
          tenants:
            agr.tenants
              ?.filter((e) => e.apartmentId == apartment.id)
              .map((e) => this.mapGet(usersData.residents, 'tenants', e.id)) || [],
          archivedAgreements,
          futureAgreements
        });
      } else {
        usersRentalUnits.push({
          id: apartment.id,
          type: RentalUnitType.APARTMENT,
          unit: apartment,
          landlords: [],
          tenants: [],
          archivedAgreements,
          futureAgreements
        });
      }
    });

    usersData.parkingSpots.forEach((parkingSpot) => {
      const currentAgreements: TenancyAgreementData[] = [];
      const archivedAgreements: TenancyAgreementData[] = [];
      const futureAgreements: TenancyAgreementData[] = [];
      usersData.agreements.forEach((agreement) => {
        const details = agreement.tenancyParkingSpotDetails?.find((details) => details.parkingSpotId === parkingSpot.id);
        if (details) {
          if (UserHlp.isCurrentAgreement(agreement) && details.rentStartDate <= today && details.rentEndDate >= today) {
            currentAgreements.push(agreement);
          } else if (details.rentStartDate > today) futureAgreements.push(agreement);
          else archivedAgreements.push(agreement);
        }
      });

      if (currentAgreements.length > 1) {
        this.errorHandlingService.showGlobalError(
          this.ls.trReplace('error.unitAgr', {
            $type: GrammarHelper.unitTypeHR(RentalUnitType.PARKING, EstateType.PRS, this.ls),
            $number: parkingSpot.id
          })
        );
      }

      if (currentAgreements.length) {
        const agr = currentAgreements[0];
        const tenancyDetails = agr.tenancyParkingSpotDetails?.find((x) => x.parkingSpotId == parkingSpot.id);
        usersRentalUnits.push({
          id: parkingSpot.id,
          type: RentalUnitType.PARKING,
          agreement: agr,
          tenancyDetails: { ...tenancyDetails, unitId: parkingSpot.id, type: RentalUnitType.PARKING },
          unit: parkingSpot,
          landlords: agr.landlords.map((e) => this.mapGet(usersData.residents, 'landlords', e.id)),
          tenants:
            agr.tenants
              ?.filter((e) => e.parkingSpot == parkingSpot.id)
              .map((e) => this.mapGet(usersData.residents, 'tenants', e.id)) || [],
          archivedAgreements,
          futureAgreements
        });
      } else {
        usersRentalUnits.push({
          id: parkingSpot.id,
          type: RentalUnitType.PARKING,
          unit: parkingSpot,
          landlords: [],
          tenants: [],
          archivedAgreements,
          futureAgreements
        });
      }
    });

    usersData.storages.forEach((storage) => {
      const currentAgreements: TenancyAgreementData[] = [];
      const archivedAgreements: TenancyAgreementData[] = [];
      const futureAgreements: TenancyAgreementData[] = [];
      usersData.agreements.forEach((agreement) => {
        const details = agreement.tenancyStorageDetails?.find((details) => details.storageId === storage.id);
        if (details) {
          if (UserHlp.isCurrentAgreement(agreement) && details.rentStartDate <= today && details.rentEndDate >= today) {
            currentAgreements.push(agreement);
          } else if (details.rentStartDate > today) futureAgreements.push(agreement);
          else archivedAgreements.push(agreement);
        }
      });

      if (currentAgreements.length > 1) {
        this.errorHandlingService.showGlobalError(
          this.ls.trReplace('error.unitAgr', {
            $type: GrammarHelper.unitTypeHR(RentalUnitType.STORAGE, EstateType.PRS, this.ls),
            $number: storage.id
          })
        );
      }

      if (currentAgreements.length) {
        const agr = currentAgreements[0];
        const tenancyDetails = agr.tenancyStorageDetails?.find((x) => x.storageId == storage.id);
        usersRentalUnits.push({
          id: storage.id,
          type: RentalUnitType.STORAGE,
          agreement: agr,
          tenancyDetails: { ...tenancyDetails, unitId: storage.id, type: RentalUnitType.STORAGE },
          unit: storage,
          landlords: agr.landlords.map((e) => this.mapGet(usersData.residents, 'landlords', e.id)),
          tenants:
            agr.tenants?.filter((e) => e.storage == storage.id).map((e) => this.mapGet(usersData.residents, 'tenants', e.id)) ||
            [],
          archivedAgreements,
          futureAgreements
        });
      } else {
        usersRentalUnits.push({
          id: storage.id,
          type: RentalUnitType.STORAGE,
          unit: storage,
          landlords: [],
          tenants: [],
          archivedAgreements,
          futureAgreements
        });
      }
    });

    return usersRentalUnits.sort((a, b) => {
      if (a.agreement?.agreementNumber === b.agreement?.agreementNumber) {
        return a.type?.localeCompare(b.type) ? 1 : -1;
      }

      if (a.agreement && b.agreement) {
        return a.agreement.agreementStartDate < b.agreement.agreementStartDate ? 1 : -1;
      }
      return a.agreement && b.agreement !== null ? -1 : 1;
    });
  }

  private prepareLandlordTenantData(usersData: UserPRSTabData): UserPRSList.ResidentElement[] {
    const usersLandlordTenant: UserPRSList.ResidentElement[] = [];
    const unitToVM = (u: RentalUnit, t: RentalUnitType, d: TenancyDetailsBase) => UserHelper.unitToRentalUnitVM(u, t, d);

    function addData(userDetails: ResidentData, type: ResidentType, agreements: TenancyAgreementData[]) {
      const currentAgreements: TenancyAgreementData[] = [];
      const archivedAgreements: TenancyAgreementData[] = [];
      const futureAgreements: TenancyAgreementData[] = [];

      agreements.forEach((agr) => {
        if (UserHlp.isCurrentAgreement(agr)) currentAgreements.push(agr);
        else if (UserHlp.isArchivedAgreement(agr)) archivedAgreements.push(agr);
        else futureAgreements.push(agr);
      });

      const apartments: RentalUnitVM[] = [];
      const parkingSpots: RentalUnitVM[] = [];
      const storages: RentalUnitVM[] = [];

      currentAgreements.forEach((agr) => {
        let tenAgr: AgreementTenant | null = null;
        if (type == ResidentType.TENANT) {
          tenAgr = agr.tenants?.find((t) => t.id == userDetails.id) ?? null;
        }
        agr.tenancyApartmentDetails?.forEach((apt) => {
          const data = usersData.apartments.get(apt.apartmentId);
          if (data && (tenAgr ? tenAgr.apartmentId == apt.apartmentId : true)) {
            apartments.push(unitToVM(data, RentalUnitType.APARTMENT, apt));
          }
        });
        agr.tenancyParkingSpotDetails?.forEach((park) => {
          const data = usersData.parkingSpots.get(park.parkingSpotId);
          if (data && (tenAgr ? tenAgr.parkingSpot == park.parkingSpotId : true)) {
            parkingSpots.push(unitToVM(data, RentalUnitType.PARKING, park));
          }
        });
        agr.tenancyStorageDetails?.forEach((sto) => {
          const data = usersData.storages.get(sto.storageId);
          if (data && (tenAgr ? tenAgr.storage == sto.storageId : true)) {
            storages.push(unitToVM(data, RentalUnitType.STORAGE, sto));
          }
        });
      });

      if (currentAgreements.length || archivedAgreements.length || futureAgreements.length) {
        usersLandlordTenant.push({
          id: userDetails.id,
          resident: userDetails,
          type,
          currentAgreements,
          archivedAgreements,
          futureAgreements,
          apartments,
          parkingSpots,
          storages
        });
      }
    }

    usersData.residents.forEach((resident) => {
      const landlordAgreement = usersData.agreements.filter((agr) => agr.landlords.map((e) => e.id)?.includes(resident.id));
      addData(resident, ResidentType.LANDLORD, landlordAgreement);

      const tenantAgreement = usersData.agreements.filter((agr) => agr.tenants?.map((e) => e.id).includes(resident.id));
      addData(resident, ResidentType.TENANT, tenantAgreement);
    });

    return usersLandlordTenant.sort((a, b) => a.resident.name.localeCompare(b.resident.name));
  }

  // #endregion Initializers

  private mapGet<T, U>(map: Map<T, U>, name: string, key: T): U {
    const value = map.get(key);
    if (!value) console.warn(`Key ${key} does not exists in ${name}`);
    return value as U;
  }

  private unitsGet(
    map: Map<string, RentalUnit>,
    type: RentalUnitType,
    id: string,
    details: TenancyDetailsBase
  ): RentalUnitVM | null {
    const val = this.mapGet(map, type, id);
    return val ? UserHelper.unitToRentalUnitVM(val, type, details) : null;
  }
}
